/**
 * API client for pricing endpoints
 */

import { api, ensureApiAuthenticated } from '../services/api';

export interface PricingJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'pending_retry' | 'paused' | 'expired' | 'committed' | 'discarded';
  channel?: string | null;
  job_name?: string | null;
  variant_count: number;
  success_count: number;
  error_count: number;
  skipped_count: number;
  retry_count: number;
  html_report_path?: string | null;
  csv_export_path?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface PricingJobDetail extends PricingJob {
  config?: Record<string, unknown> | null;
  error_details?: string | null;
}

export interface PricingJobsListParams {
  limit?: number;
  offset?: number;
  status?: string;
  channel?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * List repricing jobs
 */
export async function listPricingJobs(
  params: PricingJobsListParams = {}
): Promise<PaginatedResponse<PricingJob>> {
  await ensureApiAuthenticated();
  
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  if (params.status) queryParams.append('status', params.status);
  if (params.channel) queryParams.append('channel', params.channel);
  
  const url = `/pricing/jobs?${queryParams.toString()}`;
  const response = await api.get(url);
  
  return {
    items: response.data.items ?? [],
    total: response.data.total ?? 0,
    limit: response.data.limit ?? params.limit ?? 50,
    offset: response.data.offset ?? params.offset ?? 0,
  };
}

/**
 * Get a specific repricing job
 */
export async function getPricingJob(jobId: string): Promise<PricingJobDetail> {
  await ensureApiAuthenticated();
  
  const response = await api.get(`/pricing/jobs/${jobId}`);
  return response.data;
}

/**
 * Update repricing job (cancel, pause, resume)
 */
export async function updatePricingJob(
  jobId: string,
  action: 'cancel' | 'pause' | 'resume'
): Promise<PricingJob> {
  await ensureApiAuthenticated();
  
  const response = await api.patch(`/pricing/jobs/${jobId}`, { action });
  return response.data;
}

/**
 * Download HTML report for a job
 */
export function getReportUrl(jobId: string, reportPath: string): string {
  return reportPath.startsWith('http') ? reportPath : `/api/v1/pricing/jobs/${jobId}/report`;
}

/**
 * Download CSV export for a job
 */
export function getExportUrl(jobId: string, exportPath: string): string {
  return exportPath.startsWith('http') ? exportPath : `/api/v1/pricing/jobs/${jobId}/export`;
}

export interface AsyncJobAccepted {
  accepted: boolean;
  message: string;
  status: string;
}

export interface AsyncJobStatus {
  status: string;
  phase: string;
  message: string;
  progress_percent: number;
  started_at?: string | null;
  finished_at?: string | null;
  last_error?: string | null;
  result?: Record<string, unknown> | null;
  status_backend?: string | null;
  shared?: boolean | null;
  [key: string]: unknown;
}

const ACTIVE_ASYNC_STATUSES = new Set(['accepted', 'running', 'queued']);
const SUCCESS_ASYNC_STATUSES = new Set(['success', 'partial']);
const FAILED_ASYNC_STATUSES = new Set(['failed', 'error']);

export function isAsyncJobActive(status: string): boolean {
  return ACTIVE_ASYNC_STATUSES.has(status.trim().toLowerCase());
}

export function isAsyncJobSuccess(status: string): boolean {
  return SUCCESS_ASYNC_STATUSES.has(status.trim().toLowerCase());
}

export function isAsyncJobFailed(status: string): boolean {
  return FAILED_ASYNC_STATUSES.has(status.trim().toLowerCase());
}

/** Poll until a server-side job leaves accepted/running. */
export async function pollAsyncJobStatus(
  fetchStatus: () => Promise<AsyncJobStatus>,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    onUpdate?: (status: AsyncJobStatus) => void;
  } = {},
): Promise<AsyncJobStatus> {
  const intervalMs = options.intervalMs ?? 2000;
  const maxAttempts = options.maxAttempts ?? 900; // ~30m at 2s
  let last: AsyncJobStatus | null = null;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    last = await fetchStatus();
    options.onUpdate?.(last);
    const status = String(last.status || '').toLowerCase();
    if (!isAsyncJobActive(status)) {
      return last;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(
    `Timed out waiting for server job (last status: ${last?.status || 'unknown'})`,
  );
}

/** Start preferred refresh on the server; poll GET .../refresh/status. */
export async function refreshPreferredPrices(): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/preferred/refresh');
  return response.data as AsyncJobAccepted;
}

export async function fetchPreferredRefreshStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/preferred/refresh/status');
  return response.data as AsyncJobStatus;
}

/** Start Manapool listing-price sync on the server; poll GET .../sync/manapool/status. */
export async function syncManapoolListingPrices(): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/sync/manapool');
  return response.data as AsyncJobAccepted;
}

export async function fetchManapoolListingSyncStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/sync/manapool/status');
  return response.data as AsyncJobStatus;
}

/** Start Manapool market-intel sync on the server; poll GET .../market-intel/status. */
export async function syncManapoolMarketIntel(
  productLimit = 2000,
): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/sync/manapool/market-intel', null, {
    params: { product_limit: productLimit },
  });
  return response.data as AsyncJobAccepted;
}

export async function fetchManapoolMarketIntelStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/sync/manapool/market-intel/status');
  return response.data as AsyncJobStatus;
}

export interface TcgplayerSellerSessionStatus {
  status: string;
  updated_at?: string | null;
  stale: boolean;
  stale_reason?: string | null;
  has_auth_ticket?: boolean;
  source?: string | null;
  cookie_names?: string[];
}

/** Non-secret freshness of Vault-backed TCGPlayer seller session (extension pushes cookies). */
export async function fetchTcgplayerSellerSessionStatus(): Promise<TcgplayerSellerSessionStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/tcgplayer/seller-session/status');
  return response.data as TcgplayerSellerSessionStatus;
}

/** Start TCG Custom Export + ingest on the worker; poll GET .../pricing-export/status. */
export async function runTcgplayerPricingExport(): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/tcgplayer/pricing-export/run');
  return response.data as AsyncJobAccepted;
}

export async function fetchTcgplayerPricingExportStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/tcgplayer/pricing-export/status');
  return response.data as AsyncJobStatus;
}

/** Immediate policy stamp + fan-out (scheduler / emergency). UI should preview first. */
export async function refreshPolicyPrices(): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/policy/refresh');
  return response.data as AsyncJobAccepted;
}

export async function fetchPolicyPricingStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/policy/refresh/status');
  return response.data as AsyncJobStatus;
}

/** Dry-run policy delta. Poll GET .../preview/status, then apply with preview_id. */
export async function previewPolicyPrices(): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/policy/preview');
  return response.data as AsyncJobAccepted;
}

export async function fetchPolicyPricingPreviewStatus(): Promise<AsyncJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/policy/preview/status');
  return response.data as AsyncJobStatus;
}

export interface PolicyPreviewDiscardResult {
  discarded: boolean;
  preview_id?: string | null;
  message: string;
}

/** Drop stored preview rows without applying. */
export async function discardPolicyPreview(): Promise<PolicyPreviewDiscardResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/policy/preview/discard');
  return response.data as PolicyPreviewDiscardResult;
}

export interface ClearPreferredOverridesResult {
  scope: string;
  dry_run: boolean;
  total_found: number;
  cleared: number;
  sell_base_changed: number;
  pricing_events_emitted: number;
  message: string;
}

/** Clear variant preferred_price_override stamps in bulk (sellable inventory by default). */
export async function clearPreferredPriceOverrides(params?: {
  dryRun?: boolean;
  scope?: 'sellable_only' | 'all';
  limit?: number;
}): Promise<ClearPreferredOverridesResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/policy/clear-overrides', null, {
    params: {
      dry_run: params?.dryRun ?? false,
      scope: params?.scope ?? 'all',
      limit: params?.limit ?? 20000,
    },
  });
  return response.data as ClearPreferredOverridesResult;
}

export interface AcquisitionPackRebaseResult {
  set_name: string;
  dry_run: boolean;
  pack_cost: number;
  cards_per_pack: number;
  new_acquisition_cost: number;
  copies_matched: number;
  copies_updated: number;
  old_min_cost?: number | null;
  old_max_cost?: number | null;
  old_avg_cost?: number | null;
  sample: Array<{
    copy_id: string;
    product_name?: string | null;
    set_name?: string | null;
    condition?: string | null;
    old_acquisition_cost?: number | null;
    new_acquisition_cost: number;
  }>;
  message: string;
}

/** Rebase sellable-copy acquisition costs to pack_cost / cards_per_pack for a set (ILIKE). */
export async function rebaseAcquisitionPackCosts(body: {
  set_name: string;
  pack_cost: number;
  cards_per_pack?: number;
  dry_run?: boolean;
}): Promise<AcquisitionPackRebaseResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/acquisition/rebase-pack', {
    set_name: body.set_name,
    pack_cost: body.pack_cost,
    cards_per_pack: body.cards_per_pack ?? 15,
    dry_run: body.dry_run ?? true,
  });
  return response.data as AcquisitionPackRebaseResult;
}

export type PolicyAnomalyFlag =
  | 'large_move'
  | 'high_vs_market'
  | 'low_vs_market'
  | 'no_low_comp'
  | 'outlier_rejected';

export interface PolicyPreviewRow {
  variant_id: string;
  product_name?: string | null;
  set_name?: string | null;
  card_number?: string | null;
  tcgplayer_product_id?: string | null;
  tcgplayer_sku_id?: string | null;
  manapool_product_id?: string | null;
  condition?: string | null;
  printing?: string | null;
  foil?: boolean | null;
  etched?: boolean | null;
  language?: string | null;
  age_days?: number | null;
  copy_count?: number | null;
  acquisition_cost_min?: number | null;
  acquisition_cost_max?: number | null;
  current_policy_price?: number | null;
  proposed_policy_price?: number | null;
  policy_delta?: number | null;
  policy_delta_dollars?: number | null;
  current_sell_base?: number | null;
  proposed_sell_base?: number | null;
  sell_base_changes?: boolean;
  override_holds?: boolean;
  override_price?: number | null;
  skipped_no_signals?: boolean;
  market_ref?: number | null;
  pct_policy_change?: number | null;
  pct_vs_market?: number | null;
  anomalies?: PolicyAnomalyFlag[];
  needs_review?: boolean;
  debug?: Record<string, unknown> | null;
}

export interface PolicyApplyAdjustment {
  variant_id: string;
  proposed_policy_price?: number | null;
  skip?: boolean;
}

export interface PolicyPreviewRowsPage {
  preview_id?: string | null;
  total: number;
  skip: number;
  limit: number;
  rows: PolicyPreviewRow[];
  summary?: Record<string, unknown>;
}

export async function fetchPolicyPricingPreviewRows(params: {
  skip?: number;
  limit?: number;
  changedOnly?: boolean;
  bucket?: 'increases' | 'decreases' | 'overrides' | 'needs_review' | 'anomalies';
} = {}): Promise<PolicyPreviewRowsPage> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/policy/preview/rows', {
    params: {
      skip: params.skip ?? 0,
      limit: params.limit ?? 20000,
      changed_only: params.changedOnly ?? true,
      bucket: params.bucket,
    },
  });
  return response.data as PolicyPreviewRowsPage;
}

/** Apply stored preview merged with operator skip/price edits. */
export async function applyPolicyPrices(
  previewId: string,
  adjustments: PolicyApplyAdjustment[] = [],
): Promise<AsyncJobAccepted> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/policy/apply', {
    preview_id: previewId,
    adjustments,
  });
  return response.data as AsyncJobAccepted;
}

export interface ManapoolBulkPriceDefaults {
  ui_mapping: Record<string, unknown>;
  payload: {
    filters?: Record<string, unknown>;
    pricing?: { strategy?: string; modifier?: number; [key: string]: unknown };
    excludeLetterShippingDisabledSellers?: boolean;
  };
  admin_enforcement_enabled: boolean;
  workflow: {
    steps: string[];
    preview_ttl_minutes: number;
    docs?: string;
  };
  notes: string;
}

export interface ManapoolBulkPricePreviewResult {
  preview_id: string;
  expires_at: string;
  request: Record<string, unknown>;
  preview: Record<string, unknown>;
}

export interface ManapoolBulkPriceApplyResult {
  preview_id: string;
  request: Record<string, unknown>;
  bulk_price: { jobId?: string; job_id?: string; [key: string]: unknown };
  next_steps?: Record<string, unknown>;
}

export interface ManapoolBulkPriceJobStatus {
  job?: {
    id?: string;
    status?: string;
    [key: string]: unknown;
  };
  status?: string;
  [key: string]: unknown;
}

export interface ManapoolBulkPriceExportSyncResult {
  success: boolean;
  job_id: string;
  export_format?: string;
  rows_parsed?: number;
  rows_considered?: number;
  copies_updated?: number;
  variants_updated?: number;
  skipped?: number;
  unmatched?: number;
  errors?: string[];
  shopify_fanout_targets?: string[];
  shopify_events_emitted?: number;
  preferred_price_unchanged?: boolean;
  export_meta?: Record<string, unknown>;
}

/** Canonical SyncBase defaults for Manapool auto-reprice. */
export async function getManapoolBulkPriceDefaults(): Promise<ManapoolBulkPriceDefaults> {
  await ensureApiAuthenticated();
  const response = await api.get('/pricing/manapool/bulk-price/defaults');
  return response.data as ManapoolBulkPriceDefaults;
}

/** Preview a Manapool bulk-price job; returns redeemable preview_id. */
export async function previewManapoolBulkPrice(
  body: Record<string, unknown> = {},
): Promise<ManapoolBulkPricePreviewResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/manapool/bulk-price/preview', body);
  return response.data as ManapoolBulkPricePreviewResult;
}

/** Apply a previously previewed Manapool bulk-price job. */
export async function applyManapoolBulkPrice(body: {
  preview_id: string;
  confirm: true;
  filters?: Record<string, unknown>;
  pricing?: Record<string, unknown>;
  excludeLetterShippingDisabledSellers?: boolean;
}): Promise<ManapoolBulkPriceApplyResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/manapool/bulk-price', body);
  return response.data as ManapoolBulkPriceApplyResult;
}

export interface ManapoolBulkPriceDryRunResult {
  is_preview: true;
  request: Record<string, unknown>;
  manapool_request: Record<string, unknown>;
  bulk_price: { jobId?: string; job_id?: string; [key: string]: unknown };
  job_id: string | null;
  next_steps?: Record<string, unknown>;
}

export interface ManapoolBulkPriceExportRowsResult {
  success: boolean;
  job_id: string;
  export_format?: string;
  rows_parsed?: number;
  items: Record<string, unknown>[];
  export_meta?: Record<string, unknown>;
}

/**
 * Start a Manapool isPreview dry-run job (no price changes).
 * Poll GET …/jobs/{job_id}, then GET …/export-rows for the full item list.
 */
export async function dryRunManapoolBulkPrice(
  body: Record<string, unknown> = {},
): Promise<ManapoolBulkPriceDryRunResult> {
  await ensureApiAuthenticated();
  const response = await api.post('/pricing/manapool/bulk-price/dry-run', body);
  return response.data as ManapoolBulkPriceDryRunResult;
}

/** Parsed full item list from a completed job export (no SyncBase stamp). */
export async function getManapoolBulkPriceExportRows(
  jobId: string,
): Promise<ManapoolBulkPriceExportRowsResult> {
  await ensureApiAuthenticated();
  const response = await api.get(`/pricing/manapool/bulk-price/jobs/${jobId}/export-rows`);
  return response.data as ManapoolBulkPriceExportRowsResult;
}

/** Poll Manapool bulk-price job status. */
export async function getManapoolBulkPriceJob(
  jobId: string,
): Promise<ManapoolBulkPriceJobStatus> {
  await ensureApiAuthenticated();
  const response = await api.get(`/pricing/manapool/bulk-price/jobs/${jobId}`);
  return response.data as ManapoolBulkPriceJobStatus;
}

/** Stamp SyncBase listing prices from completed job export + Shopify fan-out. */
export async function syncManapoolBulkPriceFromExport(
  jobId: string,
  emitShopifyFanout = true,
): Promise<ManapoolBulkPriceExportSyncResult> {
  await ensureApiAuthenticated();
  const response = await api.post(
    `/pricing/manapool/bulk-price/jobs/${jobId}/sync-from-export`,
    null,
    { params: { emit_shopify_fanout: emitShopifyFanout } },
  );
  return response.data as ManapoolBulkPriceExportSyncResult;
}

export function extractManapoolBulkJobId(
  applyResult: ManapoolBulkPriceApplyResult | ManapoolBulkPriceDryRunResult,
): string | null {
  if ('job_id' in applyResult && applyResult.job_id) {
    return String(applyResult.job_id);
  }
  const raw =
    applyResult.bulk_price?.jobId ??
    applyResult.bulk_price?.job_id ??
    null;
  return raw != null ? String(raw) : null;
}

export function extractManapoolBulkJobStatus(
  payload: ManapoolBulkPriceJobStatus,
): string {
  const nested = payload.job?.status;
  if (typeof nested === 'string' && nested.trim()) return nested.trim().toLowerCase();
  if (typeof payload.status === 'string' && payload.status.trim()) {
    return payload.status.trim().toLowerCase();
  }
  return '';
}

const COMPLETED_JOB_STATUSES = new Set(['completed', 'complete', 'succeeded', 'success']);

export function isManapoolBulkJobCompleted(status: string): boolean {
  return COMPLETED_JOB_STATUSES.has(status.trim().toLowerCase());
}

const FAILED_JOB_STATUSES = new Set(['failed', 'error', 'cancelled', 'canceled']);

export function isManapoolBulkJobFailed(status: string): boolean {
  return FAILED_JOB_STATUSES.has(status.trim().toLowerCase());
}
